import React, { useState, useEffect } from 'react';
import { Globe, ArrowUpRight, Menu, X } from 'lucide-react';
import { TRANSLATIONS } from './translations';
import Portal from './Portal';

const Navbar = ({
    onNavigateHome,
    onNavigateToPackages,
    onNavigateToDestinations,
    onNavigateToServices,
    onNavigateToFAQ,
    onNavigateToContact,
    onNavigateToAbout,
    lang
}: {
    onNavigateHome?: () => void;
    onNavigateToPackages?: () => void;
    onNavigateToDestinations?: () => void;
    onNavigateToServices?: () => void;
    onNavigateToFAQ?: () => void;
    onNavigateToContact?: () => void;
    onNavigateToAbout?: () => void;
    lang: 'en' | 'ar';
}) => {
    const currentLang = TRANSLATIONS[lang] ? lang : 'en';
    const t = TRANSLATIONS[currentLang].nav;
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen]);

    const handleNavClick = (link: string) => {
        if (link === t.home && onNavigateHome) {
            onNavigateHome();
        } else if (link === t.destinations && onNavigateToDestinations) {
            onNavigateToDestinations();
        } else if (link === t.bestOffers && onNavigateToPackages) {
            onNavigateToPackages();
        } else if (link === t.services && onNavigateToServices) {
            onNavigateToServices();
        } else if (link === t.faq && onNavigateToFAQ) {
            onNavigateToFAQ();
        } else if (link === t.contact && onNavigateToContact) {
            onNavigateToContact();
        } else if (link === t.about && onNavigateToAbout) {
            onNavigateToAbout();
        }
        setIsMobileMenuOpen(false);
    };

    const handleLanguageSwitch = () => {
        const newPath = lang === 'ar' ? '/en' : '/';
        // Use window.location for full reload to ensure language change propagates correctly throughout the app
        // or use navigation if it was SPA routed, but based on App.tsx it seems it might be doing a reload.
        // App.tsx uses:
        // window.history.pushState({}, '', newPath);
        // window.location.reload();
        // I will replicate that logic or accept a callback.
        // For now, replicating the logic as it was self-contained in the component in App.tsx.

        window.history.pushState({}, '', newPath);
        window.location.reload();
    };

    return (
        <>
            <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4 bg-white/95 backdrop-blur-md shadow-sm" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                {/* Logo & App Pill */}
                <div className="flex items-center gap-6 z-50 relative">
                    <div
                        onClick={onNavigateHome}
                        className="flex items-center gap-2 cursor-pointer select-none"
                    >
                        <img src="/assets/images/logo_como.png" alt="COMO" className="h-11 w-auto object-contain" width="120" height="44" />
                    </div>
                </div>

                {/* Center: Links (Desktop) */}
                <div className="hidden md:flex items-center gap-6">
                    {[t.home, t.bestOffers, t.destinations, t.services, t.faq, t.contact, t.about, t.blog].map((link) => (
                        <button
                            key={link}
                            onClick={() => handleNavClick(link)}
                            className="text-sm font-semibold text-gray-500 hover:text-gray-900 cursor-pointer transition-colors bg-transparent border-none"
                        >
                            {link}
                        </button>
                    ))}
                </div>

                {/* Right: Sign Up & Lang Switch (Desktop) */}
                <div className="hidden md:flex items-center gap-4">
                    <button
                        onClick={handleLanguageSwitch}
                        className="text-sm font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1 bg-transparent border-none cursor-pointer"
                    >
                        <Globe className="w-4 h-4" /> {lang === 'ar' ? 'English' : 'العربية'}
                    </button>
                    <a href="/login" className="bg-emerald-700 text-white px-6 py-2.5 rounded-full font-bold text-sm hover:bg-emerald-800 hover:shadow-lg transition-all active:scale-95 flex items-center gap-1 group no-underline">
                        {t.signup} <ArrowUpRight className={`w-4 h-4 transition-transform ${lang === 'ar' ? 'rotate-180 group-hover:-translate-x-0.5 group-hover:-translate-y-0.5' : 'group-hover:translate-x-0.5 group-hover:-translate-y-0.5'}`} />
                    </a>
                </div>

                {/* Mobile Menu Toggle */}
                <div className="flex md:hidden items-center gap-3">
                    <button
                        onClick={() => setIsMobileMenuOpen(true)}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                        aria-label={lang === 'ar' ? 'فتح القائمة' : 'Open menu'}
                    >
                        <Menu className="w-6 h-6 text-gray-900" />
                    </button>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                <Portal>
                    <div className="fixed inset-0 z-[100] bg-white/95 backdrop-blur-xl flex flex-col p-6 animate-in fade-in duration-200" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                        <div className="flex justify-between items-center mb-8">
                            <div className="flex items-center gap-2">
                                <img src="/assets/images/logo_como.png" alt="COMO" className="h-10 w-auto object-contain" width="109" height="40" />
                            </div>
                            <button
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                                aria-label={lang === 'ar' ? 'إغلاق القائمة' : 'Close menu'}
                            >
                                <X className="w-6 h-6 text-gray-600" />
                            </button>
                        </div>

                        {/* Links */}
                        <div className="flex flex-col gap-6 text-xl font-bold text-gray-900">
                            {[t.home, t.bestOffers, t.destinations, t.services, t.faq, t.contact, t.about, t.blog].map((link) => (
                                <button
                                    key={link}
                                    onClick={() => handleNavClick(link)}
                                    className="cursor-pointer hover:text-emerald-600 transition-colors bg-transparent border-none text-left text-xl font-bold text-gray-900"
                                >
                                    {link}
                                </button>
                            ))}
                        </div>

                        <hr className="my-8 border-gray-200" />

                        {/* Language & Action */}
                        <div className="flex flex-col gap-6 mt-auto">
                            <button
                                onClick={() => { handleLanguageSwitch(); setIsMobileMenuOpen(false); }}
                                className="text-lg font-bold text-gray-600 hover:text-gray-900 flex items-center gap-2 bg-transparent border-none cursor-pointer"
                            >
                                <Globe className="w-5 h-5" /> {lang === 'ar' ? 'English' : 'العربية'}
                            </button>
                            <a href="/login" className="bg-emerald-700 text-white w-full py-4 rounded-xl font-bold text-lg hover:bg-emerald-800 hover:shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 group no-underline">
                                {t.signup} <ArrowUpRight className={`w-5 h-5 transition-transform ${lang === 'ar' ? 'rotate-180' : ''}`} />
                            </a>
                        </div>
                    </div>
                </Portal>
            )}
        </>
    );
};

export default Navbar;
